import React from 'react';
import clsx from 'clsx';
import { Typography, Link as MuiLink } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { NavLayout } from '../../components';
import useStyles from './useStyles';

const PrivacyPolicy: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('legal');

  return (
    <NavLayout>
      <React.Fragment>
        <Typography
          variant="h1"
          className={clsx(
            classes.colorPrimary,
            classes.backgroundImage,
            classes['marginBottom-5']
          )}>
          {t('privacy policy')}
        </Typography>

        <div className={classes.marginHorizontal2UpSm}>
          <p dir="ltr">Updated April 06, 2020</p>
          <p dir="ltr">
            This Privacy Policy ("Policy") describes how how QuickCells
            ("QuickCells", "we," or "us") collects, uses and discloses your
            personal information. This Policy applies to information we collect
            when you use our websites, mobile applications and other online
            products and services where this Policy is posted (collectively, the
            "Services").
          </p>
          <p dir="ltr">
            We may change the provisions of this Policy at any time and will
            indicate when changes have been made by revising the date at the top
            of this Policy. We encourage you to review the Policy whenever you
            access the Services to make sure that you understand our information
            collection, use and disclosure practices. If we make material
            changes to this policy, we will provide you with additional notice
            of such changes.
          </p>
          <h3 dir="ltr">Information We Collect</h3>
          <p dir="ltr">
            <em>Information That You Provide to Us</em>
          </p>
          <p dir="ltr">
            QuickCells may collect information that you provide when you use the
            Services, such as when you: (1) create an account; (2) make a
            purchase; (3) participate in events or promotions; (4) send
            questions or comments via e-mail or live chat to QuickCells customer
            support; (5) apply for a job with us online; (6) fill out surveys;
            or (7) otherwise communicate with us through or about the Services.
            The types of personal information that you provide may include your
            name, e-mail address, telephone number, postal address, credit card
            information, and other contact or identifying information that you
            choose to provide.
          </p>
          <p dir="ltr">
            QuickCells stores, processes and maintains files that you create
            and/or upload using the Services (as well as previous versions of
            your files), including QuickCells documents that you create, sharing
            lists, and other data related to your account in order to provide
            the service to you.
          </p>
          <p dir="ltr">
            <em>Information About Use of Our Services</em>
          </p>
          <p dir="ltr">
            QuickCells’s servers automatically record certain information about
            your use of the Services. QuickCells uses a variety of technologies
            to collect this information, including persistent and session
            cookies, web beacons, and pixel tracking technology. The information
            we collect includes storage usage, number of log-ins, actions taken,
            data displayed or clicked on (e.g., UI elements, links), and other
            login information (e.g., browser type, IP address, date and time of
            access, cookie ID, referrer URL, etc.). QuickCells may collect
            automated error reports in the case of software malfunction; such
            error reports may contain some or all of the information in your
            documents and may be reviewed to help resolve problems with the
            QuickCells applications or service.&nbsp;
          </p>
          <p dir="ltr">
            When you participate in a videoconference with us, we may record the
            session and retain recordings of the session if we notify you that
            the session is being recorded so that you can opt out.
          </p>
          <p dir="ltr">
            If you participate in a training or product course, we collect
            course completion data.
          </p>
          <h3 dir="ltr">Information We Collect from Third Parties</h3>
          <p dir="ltr">
            We may obtain information from other sources and combine that with
            information we collect through our Services. For example:
          </p>
          <ul>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                If you create or log into your account through a third-party
                social networking site or one of our integration partners, we
                will have access to certain information from that service, such
                as your name and account information.&nbsp;
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                If you elect to purchase a license to use our products, we may
                receive information about your purchase from our third-party
                payment processor.
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                We may collect information from unaffiliated third parties so we
                can better understand you and provide you with information and
                offers that may be of interest to you. For example, we may
                receive information about your company, company size, job title,
                and job level as well as contact information, such as email
                address or phone number.&nbsp;
              </p>
            </li>
          </ul>
          <p dir="ltr">
            Please note that all of the information we collect about you may be
            combined and used for the purposes described in the "How We Use Your
            Information" section below.
          </p>
          <h3 dir="ltr">How We Use the Information We Collect</h3>
          <p dir="ltr">
            We use personal information collected through our Services for
            purposes described in this Policy or otherwise disclosed to you on
            or in connection with our Services. For example, we may use your
            information to:
          </p>
          <ul>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Operate and improve our Services;
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Send you advertising or promotional materials, including
                information about new products, contests, features and
                enhancements, special offers and other events of interest from
                QuickCells and our select partners;
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Provide and deliver the products and Services you request,
                process transactions, and to send you related information,
                including confirmations and invoices;
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Send you technical notices, updates, security alerts and support
                and administrative messages;
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Respond to your comments, questions and requests and provide
                customer service;
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Monitor and evaluate trends, usage and activities in connection
                with our Services;
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Personalize and improve the Services and provide content,
                communications or features that match user profiles or
                interests; and
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Link or combine with other information we get from third parties
                to help understand your needs and provide you with better
                service.
              </p>
            </li>
          </ul>
          <p dir="ltr">
            QuickCells reserves the right to review documents to help resolve
            problems with our software or Services, or to ensure compliance with
            our Terms of Service.
          </p>
          <h3 dir="ltr">Information Sharing</h3>
          <p dir="ltr">
            QuickCells may share your personal information with third parties in
            the following circumstances:
          </p>
          <ul>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Files you create, upload, or copy into our products may, if you
                choose, be read, copied, used and redistributed by people you
                know or, again if you choose, by people you do not know.
                Information you disclose using the chat function of the Services
                may be read, copied, used and redistributed by people
                participating in the chat. Use care when including sensitive
                personal information, such as home addresses or phone numbers,
                in files you share or in chat sessions.
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Some features of our Services may be provided by third parties
                (e.g., payments processing, sharing features of social networks
                such as Facebook and Google+, integration features, etc.), who
                may receive and process your data. When you use one of these
                features, you may be sharing data with the third party,
                including allowing the third party to process your data. Access
                to your data by these third parties is not governed by this
                Privacy Policy.
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                With your employer if you are a registered user, and the domain
                of the primary email address associated with your account is
                owned by your employer and that email address was assigned to
                you as an employee of that organization, and an authorized
                representative of that organization wishes to establish an
                enterprise account and add you to it, then certain information
                concerning your individual account may become accessible to that
                organization’s administrator, including your name and email
                address, and your account may be added to the corporate account.
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                With third party vendors, consultants and other service
                providers who are working on our behalf and need access to your
                information to carry out their work for us. These entities have
                agreed to maintain the confidentiality, security, and integrity
                of the personal information they obtain from us, and, unless we
                notify you otherwise and provide you with an opportunity to
                opt-out, will not use your personal information for any purpose
                other than as described in this Policy.
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                With data analytics and advertising services in order to
                understand your preferences and to show you advertising about
                our Services.
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                With law enforcement, courts of competent jurisdiction, or
                others when we have a good faith belief that access, use,
                preservation or disclosure of such information is reasonably
                necessary to (a) satisfy any applicable law, regulation, legal
                process or enforceable governmental request, (b) enforce
                applicable Terms of Service, including investigation of
                potential violations thereof, (c) detect, prevent, or otherwise
                address fraud, security or technical issues, or (d) protect
                against harm to the rights, property or safety of QuickCells,
                its users or the public as required or permitted by law.
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                When we have your consent.
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                In connection with, or during negotiations of, any merger, sale
                of some or all of QuickCells’s assets, bankruptcy or
                reorganization, financing or acquisition of all or a portion of
                QuickCells’s business to another company.
              </p>
            </li>
          </ul>
          <p dir="ltr">
            We may share with third parties certain pieces of aggregated,
            non-personal information, such as the number of users who used a
            type of document or how many users clicked on a particular
            advertisement. Such information does not identify you individually.
          </p>
          <h3 dir="ltr">
            Advertising and Analytics Services Provided by Others
          </h3>
          <p dir="ltr">
            We may allow others to provide analytics services and display
            advertisements for our Services. These providers (for example,{' '}
            <MuiLink
              className={classes.colorPrimary}
              href="https://www.google.com/policies/privacy/partners/">
              Google Analytics
            </MuiLink>
            ) may use cookies, web beacons, device identifiers, and other
            tracking technologies which collect information about your use of
            our Services. This information may be used by us and others to
            determine the popularity of our Services, deliver advertising and
            content targeted to your interests in the Services and on other
            websites, apps, and other services, and to better understand your
            online activity. You can find more information about this practice
            or opt out of use of your web browsing activity for interest-based
            advertising purposes by visiting{' '}
            <MuiLink
              className={classes.colorPrimary}
              href="https://aboutads.info/choices">
              https://aboutads.info/choices
            </MuiLink>
            . You can opt out of Google Analytics by visiting{' '}
            <MuiLink
              className={classes.colorPrimary}
              href="https://tools.google.com/dlpage/gaoptout">
              https://tools.google.com/dlpage/gaoptout
            </MuiLink>
            .&nbsp;
          </p>
          <h3 dir="ltr">Data Security</h3>
          <p dir="ltr">
            QuickCells takes reasonable measures to protect your personal
            information and your documents from loss, misuse and unauthorized
            access, disclosure, alteration and destruction and to ensure that
            your documents remain available to you.
          </p>
          <h3 dir="ltr">Data Retention</h3>
          <p dir="ltr">
            We store the information we collect about you for as long as is
            necessary for the purpose(s) for which we originally collected it.
            We store our backups for six (6) months. We may retain certain
            information for legitimate business purposes or as required by law.
          </p>
          <h3 dir="ltr">
            Privacy Shield and Dispute Resolution for Users in the European
            Economic Area and Switzerland
          </h3>
          <p dir="ltr">
            Please direct any inquiries or complaints regarding our compliance
            with the Principles to the point of contact listed in the "Contact
            Us" section below.
          </p>
          <p dir="ltr">
            QuickCells is subject to the investigatory and enforcement powers of
            the EU legislation. If QuickCells shares European Data with a
            third-party service provider that processes the data solely on our
            behalf, then QuickCells will be liable for that third party’s
            processing of European Data in violation of the Principles, unless
            QuickCells can prove that it is not responsible for the event giving
            rise to the damage.
          </p>
          <p dir="ltr">
            Under the Principles, you have the right to access your information
            that QuickCells has collected from you. You may do so by contacting
            QuickCells using the "Contact Us" information below. You have the
            right to limit the use and disclosure of your personal data by not
            providing said information to QuickCells or by not using our
            Services. You may request that QuickCells correct, amend, or delete
            your information where it is inaccurate, or has been processed in
            violation of the Principles, except where the burden or expense of
            providing access would be disproportionate to the risks to your
            privacy in the case in question, or where the rights of other
            persons would be violated.
          </p>
          <h3 dir="ltr">Your Choices and Rights</h3>
          <p dir="ltr">
            You have several choices with respect to your personal information,
            as follows:&nbsp;
          </p>
          <ul>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                You may terminate your use of the Services at any time.
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Accessing, Editing, or Deleting your Personal Information. If
                you have created an account, you may at any time review and/or
                update the contact information we have for you. Please note that
                even if you delete information from your account, or deactivate
                it, we may retain certain information as required by law or for
                legitimate business purposes. We may also retain cached or
                archived copies of your information for a certain period of
                time.&nbsp;
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Promotional Communications. You may opt out of receiving
                promotional emails, text messages, or mail from QuickCells by
                visiting your user settings page at&nbsp;
                <MuiLink
                  className={classes.colorPrimary}
                  component={Link}
                  to="/dashboard/individual">
                  quickcells.co/dashboard
                </MuiLink>
                , as applicable. and updating your communications preferences,
                by following the instructions in emails or text messages, or by
                sending an e-mail to&nbsp;
                <MuiLink
                  className={classes.colorPrimary}
                  href="mailto:privacy@quickcells.co">
                  privacy@quickcells.co
                </MuiLink>
                , as applicable. If you opt out, we may still send you
                transactional or relationship messages, such as emails about
                your account or updates to our Services.
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                Cookies. Most web browsers are set to accept cookies by default.
                If you prefer, you can usually choose to set your browser to
                remove or reject browser cookies. Removing or rejecting browser
                cookies does not necessarily affect third party flash cookies
                used in connection with our Services. For more information about
                how to delete or disable flash cookies please visit
                www.adobe.com/products/flashplayer/security. Please note that if
                you choose to remove or reject cookies, this could affect the
                availability and functionality of our Services.
              </p>
            </li>
          </ul>

          <h3 dir="ltr">Residents of the European Economic Area</h3>
          <p dir="ltr">
            If you are a resident of the European Economic Area (EEA), please
            review this additional information.
          </p>
          <p dir="ltr">
            <em>Legal Basis for Processing</em>
          </p>
          <p dir="ltr">
            When we process your personal data, we will only do so in the
            following situations:
          </p>
          <ul>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                We have your consent to do so. For example, we may ask your
                consent to send you marketing communications.
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                We need to use your personal data to perform our
                responsibilities under our contract with you (e.g., processing
                payments for and providing the Services you have requested).
              </p>
            </li>
            <li dir="ltr">
              <p dir="ltr" role="presentation">
                We have a legitimate interest in processing your personal data.
                For example, we may process your personal data to communicate
                with you about changes to our Services, and to provide, secure,
                and improve our Services.
              </p>
            </li>
          </ul>
          <p dir="ltr">
            <em>Questions or Complaints&nbsp;</em>
          </p>
          <p dir="ltr">
            If you are a resident of the EEA and have a concern about our
            processing of personal data that we are not able to resolve, you
            have the right to lodge a complaint with the data privacy authority
            where you reside. For contact details of your local Data Protection
            Authority, please see:&nbsp;
            <MuiLink
              className={classes.colorPrimary}
              href="http://ec.europa.eu/justice/article-29/structure/data-protection-authorities/index_en.htm">
              http://ec.europa.eu/justice/article-29/structure/data-protection-authorities/index_en.htm.
            </MuiLink>
          </p>
          <h3 dir="ltr">Contact Us</h3>
          <p dir="ltr">
            If you have questions or concerns regarding this Policy, please
            contact QuickCells at:&nbsp;
            <MuiLink
              className={classes.colorPrimary}
              href="mailto:privacy@quickcells.co">
              privacy@quickcells.co
            </MuiLink>
            , as applicable.
          </p>
          <p>&nbsp;</p>
        </div>
      </React.Fragment>
    </NavLayout>
  );
};

export { PrivacyPolicy as default, PrivacyPolicy };
